import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

import { StatusSelect } from "@/presentation/components/board/status-select";
import { StatusDot } from "@/presentation/components/status/status-dot";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import { DELIVERY_STATUS_TONE } from "@/presentation/data/board";
import { fmtShort } from "@/presentation/data/planner";
import type { BoardFeatureRow, DeliveryStatus } from "@/infrastructure/supabase/database.types";

interface PlannerDrawerContentProps {
  feature: BoardFeatureRow;
  workstreamLabel: string;
  componentLabel: string | null;
  upstream: BoardFeatureRow[];
  downstream: BoardFeatureRow[];
  leverage: number;
  onOpen: (id: string) => void;
  onPatch: (patch: Partial<BoardFeatureRow>) => void;
  readOnly?: boolean;
}

export function PlannerDrawerContent({
  feature,
  workstreamLabel,
  componentLabel,
  upstream,
  downstream,
  leverage,
  onOpen,
  onPatch,
  readOnly = false,
}: PlannerDrawerContentProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="border-border/60 flex flex-col gap-2.5 border-b px-6 py-5">
        <span className="eyebrow">{workstreamLabel}</span>
        <h2 className="text-[17px] leading-snug font-semibold tracking-tight">{feature.title}</h2>
        {componentLabel ? (
          <span className="font-data text-muted-foreground text-[12px]">{componentLabel}</span>
        ) : null}
      </header>

      <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status">
            <StatusSelect
              value={feature.delivery_status}
              onChange={(v: DeliveryStatus) => onPatch({ delivery_status: v })}
              disabled={readOnly}
            />
          </Field>
          <Field label="Due date">
            <Input
              type="date"
              defaultValue={feature.due_date ?? ""}
              className="h-9 text-[13px]"
              disabled={readOnly}
              onBlur={(e) => {
                const next = e.target.value || null;
                if (next !== feature.due_date) onPatch({ due_date: next });
              }}
            />
          </Field>
        </div>

        <div className="bg-muted/40 flex items-center justify-between rounded-md px-3 py-2.5">
          <span className="text-[12.5px]">Total downstream leverage</span>
          <span className="font-data text-[15px] font-semibold text-[oklch(0.55_0.22_300)]">
            unblocks {leverage}
          </span>
        </div>

        <DepSection
          title={`Upstream — prerequisites (${upstream.length})`}
          icon={<ArrowUpFromLine className="size-3.5" />}
          color="var(--rag-green)"
          hint="must be done first"
          empty="No prerequisites — can start anytime."
          items={upstream}
          onOpen={onOpen}
        />
        <DepSection
          title={`Downstream — unblocks (${downstream.length} direct)`}
          icon={<ArrowDownToLine className="size-3.5" />}
          color="oklch(0.55 0.22 300)"
          hint="becomes possible after this"
          empty="Nothing depends on this."
          items={downstream}
          onOpen={onOpen}
        />
      </div>
    </div>
  );
}

function DepSection({
  title,
  icon,
  color,
  hint,
  empty,
  items,
  onOpen,
}: {
  title: string;
  icon: React.ReactNode;
  color: string;
  hint: string;
  empty: string;
  items: BoardFeatureRow[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="font-data flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase"
        style={{ color }}
      >
        {icon}
        {title}
      </div>
      {items.length === 0 ? (
        <p className="text-muted-foreground text-[12px]">{empty}</p>
      ) : (
        <>
          <p className="text-muted-foreground -mt-1 text-[11px]">{hint}</p>
          {items.map((x) => (
            <button
              key={x.id}
              type="button"
              onClick={() => onOpen(x.id)}
              className="border-border hover:border-primary/40 flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-[12px] transition-colors"
            >
              <StatusDot tone={DELIVERY_STATUS_TONE[x.delivery_status]} size="md" />
              <span className="min-w-0 flex-1 truncate">{x.title}</span>
              <span className="font-data text-muted-foreground shrink-0 text-[9px]">
                due {x.due_date ? fmtShort(new Date(x.due_date + "T00:00:00")) : "—"}
              </span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="eyebrow">{label}</Label>
      {children}
    </div>
  );
}
